import React from 'react'
import { useLocation } from 'react-router-dom'
import { captureException } from '@/lib/errorTracking'
import { logger } from '@/lib/logger'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/lib/chunkReload'
import { PRODUCT_NAME } from '@/lib/brand'

interface ErrorBoundaryProps {
  children: React.ReactNode
  /** When any value changes (e.g. the pathname), a caught error is cleared. */
  resetKeys?: ReadonlyArray<unknown>
  /** Fill the viewport (app root) or just the surrounding pane (inside Layout). */
  fullScreen?: boolean
  /** Shown instead of the recovery screen (e.g. `null` for a floating widget,
   *  so a crash there leaves the page usable rather than replacing it). */
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function keysChanged(a: ReadonlyArray<unknown> = [], b: ReadonlyArray<unknown> = []): boolean {
  return a.length !== b.length || a.some((v, i) => !Object.is(v, b[i]))
}

/**
 * Catches render crashes and shows a recovery screen instead of a white page.
 * - Stale-chunk failures (dynamic import 404 after a deploy) trigger one
 *   automatic reload, then fall through to the normal screen.
 * - Resets itself when `resetKeys` change, so navigating away from a crashed
 *   page recovers without a manual refresh.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Never reload while offline: that swaps the app for the browser's
    // "No internet" page (UX-052).
    if (isChunkLoadError(error) && !isOffline() && reloadOnceForStaleChunk()) return
    logger.error('[ErrorBoundary]', error)
    captureException(error, { componentStack: info.componentStack ?? undefined })
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.error && keysChanged(prev.resetKeys, this.props.resetKeys)) {
      this.setState({ error: null })
    }
  }

  render() {
    if (!this.state.error) return this.props.children
    if (this.props.fallback !== undefined) return this.props.fallback
    const stale = isChunkLoadError(this.state.error)
    const offline = stale && isOffline()
    return (
      <div
        role="alert"
        style={{
          background: 'rgb(var(--bg-rgb))',
          color: 'rgb(var(--fg-rgb))',
          minHeight: this.props.fullScreen === false ? '60vh' : '100vh',
          padding: '40px',
          fontFamily: 'sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ maxWidth: '480px', textAlign: 'center' }}>
          <h1 style={{ color: 'rgb(var(--fg-rgb))', fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
            {offline ? "You're offline" : stale ? 'A new version is available' : 'Something went wrong'}
          </h1>
          <p style={{ color: 'rgb(var(--fg-rgb) / 0.60)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
            {offline
              ? "This page hasn't been downloaded to this device yet. Try again when you have signal."
              : stale
              ? `${PRODUCT_NAME} was updated. Refresh to load the latest version.`
              : 'Something went wrong on our side. Refresh the page; if it keeps happening, tell your admin.'}
          </p>
          <button
            onClick={() => (offline ? this.setState({ error: null }) : window.location.reload())}
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none', borderRadius: '9999px', padding: '10px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
          >
            {offline ? 'Try again' : 'Refresh page'}
          </button>
        </div>
      </div>
    )
  }
}

/** ErrorBoundary that resets whenever the route changes. Must render inside a Router. */
export function RouteErrorBoundary({ children, fullScreen }: { children: React.ReactNode; fullScreen?: boolean }) {
  const { pathname } = useLocation()
  return (
    <ErrorBoundary resetKeys={[pathname]} fullScreen={fullScreen}>
      {children}
    </ErrorBoundary>
  )
}
